import { useState, useEffect, useRef } from 'react';

/**
 * AsyncSearchSelect
 * A debounced async search/autocomplete input to replace static <select> dropdowns
 * that would otherwise render thousands of <option> elements.
 *
 * Props:
 *  - value            : string | null  — currently selected id
 *  - valueLabel       : string | null  — human label for the selected id (shown as chip)
 *  - onSelect(item)   : (item) => void — invoked with the full selected item (or null on clear)
 *  - fetchOptions(q)  : async (query) => item[]  — returns an array of items for the query
 *  - renderOption(it) : (item) => ReactNode — custom option row; defaults to item.name
 *  - placeholder      : string
 *  - testId           : string — base data-testid for input, dropdown and clear button
 *  - disabled         : boolean
 *  - minChars         : number — minimum chars before firing the search (default 1)
 *  - debounceMs       : number — debounce delay (default 300)
 */
export function AsyncSearchSelect({
  value,
  valueLabel,
  onSelect,
  fetchOptions,
  renderOption,
  placeholder = 'Type to search...',
  testId = 'async-search',
  disabled = false,
  minChars = 1,
  debounceMs = 300,
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const containerRef = useRef(null);
  const debounceRef = useRef(null);
  const reqIdRef = useRef(0);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    // With minChars=0 we allow an empty query (fetchOptions('') can still return
    // a starter list — useful for "Myself"-style default option sets).
    if (minChars > 0 && (!query || query.length < minChars)) {
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      const myReq = ++reqIdRef.current;
      try {
        const items = await fetchOptions(query || '');
        // Ignore stale responses
        if (myReq === reqIdRef.current) {
          setResults(Array.isArray(items) ? items : []);
          setHighlight(-1);
        }
      } catch (e) {
        if (myReq === reqIdRef.current) setResults([]);
      } finally {
        if (myReq === reqIdRef.current) setLoading(false);
      }
    }, debounceMs);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, open]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSelect = (item) => {
    onSelect && onSelect(item);
    setQuery('');
    setResults([]);
    setOpen(false);
  };

  const handleClear = () => {
    onSelect && onSelect(null);
    setQuery('');
    setResults([]);
  };

  const handleKeyDown = (e) => {
    if (!open) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min((results.length || 0) - 1, h + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(0, h - 1));
    } else if (e.key === 'Enter') {
      if (highlight >= 0 && results[highlight]) {
        e.preventDefault();
        handleSelect(results[highlight]);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  // Selected state: show a "chip" with clear (×)
  if (value && valueLabel) {
    return (
      <div
        ref={containerRef}
        className="relative w-full"
        data-testid={testId}
      >
        <div className="flex items-center justify-between w-full px-3 py-2 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white">
          <span className="truncate text-sm" data-testid={`${testId}-selected-label`}>
            {valueLabel}
          </span>
          {!disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="ml-2 p-1 text-slate-400 hover:text-red-500 transition-colors"
              aria-label="Clear selection"
              data-testid={`${testId}-clear`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>
    );
  }

  // Empty state: show search input
  return (
    <div ref={containerRef} className="relative w-full" data-testid={testId}>
      <div className="relative">
        <input
          type="text"
          value={query}
          disabled={disabled}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="w-full px-4 py-2 pr-9 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
          data-testid={`${testId}-input`}
          autoComplete="off"
        />
        <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
          {loading ? (
            <svg className="w-4 h-4 animate-spin text-slate-400" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
            </svg>
          ) : (
            <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 10.5a6.5 6.5 0 11-13 0 6.5 6.5 0 0113 0z" />
            </svg>
          )}
        </div>
      </div>

      {open && (
        <div
          className="absolute z-30 mt-1 w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-lg shadow-lg max-h-64 overflow-y-auto"
          data-testid={`${testId}-dropdown`}
        >
          {loading && (
            <div className="px-3 py-2 text-sm text-slate-500 dark:text-slate-400">Searching…</div>
          )}
          {!loading && minChars > 0 && query.length < minChars && (
            <div className="px-3 py-2 text-sm text-slate-500 dark:text-slate-400">
              Type at least {minChars} character{minChars > 1 ? 's' : ''} to search
            </div>
          )}
          {!loading && (minChars === 0 || query.length >= minChars) && results.length === 0 && (
            <div className="px-3 py-2 text-sm text-slate-500 dark:text-slate-400" data-testid={`${testId}-no-results`}>
              No results found
            </div>
          )}
          {!loading &&
            results.map((item, i) => (
              <button
                key={item.id || i}
                type="button"
                onClick={() => handleSelect(item)}
                onMouseEnter={() => setHighlight(i)}
                className={`w-full text-left px-3 py-2 text-sm text-slate-700 dark:text-slate-200 border-b border-slate-100 dark:border-slate-700 last:border-b-0 transition-colors ${
                  i === highlight
                    ? 'bg-blue-50 dark:bg-blue-900/30'
                    : 'hover:bg-slate-100 dark:hover:bg-slate-700'
                }`}
                data-testid={`${testId}-option-${item.id || i}`}
              >
                {renderOption ? renderOption(item) : (item.name || item.label || item.id)}
              </button>
            ))}
        </div>
      )}
    </div>
  );
}

export default AsyncSearchSelect;
